import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/invoice_appearance';

export const TextColorSchema = z.enum(['auto', '#000000', '#ffffff']);

export const InvoiceAppearanceSchema = z.object({
  logoUrl: z.url().nullable(),
  color: z.string().nullable(),
  textColor: TextColorSchema,
});

export type InvoiceAppearance = z.infer<typeof InvoiceAppearanceSchema>;

export async function loadInvoiceAppearanceQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = InvoiceAppearanceSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'invoiceAppearance');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadInvoiceAppearance(options: QueryOptions<InvoiceAppearance> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<InvoiceAppearance, ApiError>({
    queryKey: ['invoiceAppearance'],
    queryFn: () => loadInvoiceAppearanceQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<InvoiceAppearance, ApiError, 'invoiceAppearance'>(
    'invoiceAppearance',
    queryResult,
  );
}
